import { useState, useRef } from 'react';
import { uploadImageToS3 } from '../../../../../services/s3Upload';
import { useUploadVoiceMutation, useGetRecordListQuery } from '../../../../../services/cellcast.v2';
import { useI18n } from '../../../../Utils/_helpers/useI18n';
import { S3_VOICE_NAME } from '../constants';

interface UseVoiceRecorderProps {
  rawContactId: string;
  page?: number;
  limit?: number;
  onSuccess?: (result: any) => void;
  onError?: (error: any) => void;
  /**
   * When true (default), the hook uploads automatically when recording stops.
   * When false, stopping only prepares the file and consumer must call saveRecording().
   */
  autoUploadOnStop?: boolean;
}

export const useVoiceRecorder = ({
  rawContactId,
  page = 1,
  limit = 10,
  onSuccess,
  onError,
  autoUploadOnStop = true,
}: UseVoiceRecorderProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordingStartedAtRef = useRef<number | null>(null);
  const [canStop, setCanStop] = useState(true);
  const stopGuardTimerRef = useRef<number | null>(null);
  const { t } = useI18n();
  const [uploadVoice] = useUploadVoiceMutation();
  const { data: recordList, isLoading, refetch } = useGetRecordListQuery(
    { id: rawContactId, page, limit },
    { refetchOnMountOrArgChange: true, skip: !rawContactId }
  );
  // Keep the last recorded file for manual save mode
  const recordedFileRef = useRef<File | null>(null);
  const [hasRecordedAudio, setHasRecordedAudio] = useState(false);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState<string | null>(null);

  const classifyError = (error: any): { code: string; message: string } => {
    const name = (error && (error.name || error.code)) || ''
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
      return {
        code: 'MIC_PERMISSION_DENIED',
        message: t('VOICE_RECORDER.ERROR.MIC_PERMISSION_DENIED')
      }
    }
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
      return {
        code: 'MIC_NOT_FOUND',
        message: t('COMMON.ERROR.NO_MICROPHONE')
      }
    }
    if (name === 'NotReadableError' || name === 'TrackStartError') {
      return {
        code: 'MIC_IN_USE',
        message: t('VOICE_RECORDER.ERROR.MIC_IN_USE')
      }
    }
    if (name === 'SecurityError') {
      return {
        code: 'BROWSER_BLOCKED',
        message: t('VOICE_RECORDER.ERROR.BROWSER_BLOCKED')
      }
    }
    return { code: 'UNKNOWN', message: t('VOICE_RECORDER.ERROR.UNKNOWN') }
  }

  const cleanupMedia = () => {
    try {
      const rec = mediaRecorderRef.current
      if (rec) {
        try {
          // stop any active tracks
          rec.stream.getTracks().forEach((track) => {
            if (track.readyState !== 'ended') track.stop()
          })
        } catch (_) {}
      }
    } finally {
      mediaRecorderRef.current = null
      chunksRef.current = []
      setIsRecording(false)
      setIsPaused(false)
      recordingStartedAtRef.current = null
      if (stopGuardTimerRef.current) {
        window.clearTimeout(stopGuardTimerRef.current)
        stopGuardTimerRef.current = null
      }
    }
  }

  const cleanupAfterFinish = () => {
    try {
      const rec = mediaRecorderRef.current
      if (rec) {
        try {
          rec.stream.getTracks().forEach((track) => {
            if (track.readyState !== 'ended') track.stop()
          })
        } catch (_) {}
      }
    } finally {
      mediaRecorderRef.current = null
      chunksRef.current = []
      setIsRecording(false)
      setIsPaused(false)
      recordingStartedAtRef.current = null
    }
  }

  const startRecording = async () => {
    try {
      // if there is a previous prepared audio, discard it before new recording
      if (!autoUploadOnStop && recordedAudioUrl) {
        try { URL.revokeObjectURL(recordedAudioUrl) } catch (_) {}
        setRecordedAudioUrl(null)
      }
      recordedFileRef.current = null
      setHasRecordedAudio(false)
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        try {
          // Create WAV file from recorded chunks
          const audioBlob = new Blob(chunksRef.current, { type: 'audio/wav' });
          const fileName = `voice_${rawContactId}_${Date.now()}.wav`;
          const file = new File([audioBlob], fileName, { type: 'audio/wav' });

          if (autoUploadOnStop) {
            setIsUploading(true);
            const voiceFileUrl = await uploadImageToS3(file, t, S3_VOICE_NAME);
            if (voiceFileUrl && rawContactId) {
              const result = await uploadVoice({
                voiceFile: voiceFileUrl,
                rawContact: rawContactId
              });
              onSuccess?.(result);
              refetch();
            }
          } else {
            recordedFileRef.current = file;
            setHasRecordedAudio(true);
            try {
              const url = URL.createObjectURL(audioBlob)
              setRecordedAudioUrl(url)
            } catch (_) {}
          }
        } catch (error) {
          onError?.(error);
        } finally {
          setIsUploading(false);
          // After finishing, ensure everything is released and state reset
          cleanupAfterFinish();
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setIsPaused(false);
      recordingStartedAtRef.current = Date.now();
      setCanStop(false);
      // @ts-ignore - browser setTimeout returns number
      stopGuardTimerRef.current = window.setTimeout(() => {
        setCanStop(true);
      }, 5000);
    } catch (error) {
      onError?.(classifyError(error));
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      try {
        mediaRecorderRef.current.pause();
        setIsPaused(true);
      } catch (_) {}
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && isRecording && isPaused) {
      try {
        mediaRecorderRef.current.resume();
        setIsPaused(false);
      } catch (_) {}
    }
  };

  const stopRecording = () => {
    // Enforce minimum 5s duration before stop
    if (!canStop) {
      return;
    }
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
      setIsPaused(false);
      recordingStartedAtRef.current = null;
      if (stopGuardTimerRef.current) {
        window.clearTimeout(stopGuardTimerRef.current);
        stopGuardTimerRef.current = null;
      }
    }
  };

  const saveRecording = async () => {
    if (!recordedFileRef.current) return;
    try {
      setIsUploading(true);
      const voiceFileUrl = await uploadImageToS3(recordedFileRef.current, t, S3_VOICE_NAME);
      if (voiceFileUrl && rawContactId) {
        const result = await uploadVoice({
          voiceFile: voiceFileUrl,
          rawContact: rawContactId
        });
        onSuccess?.(result);
        setHasRecordedAudio(false);
        recordedFileRef.current = null;
        if (recordedAudioUrl) {
          try { URL.revokeObjectURL(recordedAudioUrl) } catch (_) {}
        }
        setRecordedAudioUrl(null);
        refetch();
      }
    } catch (error) {
      onError?.(error);
    } finally {
      setIsUploading(false);
    }
  }

  const discardRecording = () => {
    recordedFileRef.current = null;
    setHasRecordedAudio(false);
    if (recordedAudioUrl) {
      try { URL.revokeObjectURL(recordedAudioUrl) } catch (_) {}
    }
    setRecordedAudioUrl(null);
  }

  // Force stop regardless of canStop or current state (used on modal close)
  const forceStopRecording = () => {
    try {
      if (mediaRecorderRef.current) {
        try { mediaRecorderRef.current.stop(); } catch (_) {}
      }
    } finally {
      cleanupMedia();
    }
  }

  // Upload an externally provided file directly (skips recording)
  const uploadFile = async (file: File) => {
    try {
      setIsUploading(true)
      const voiceFileUrl = await uploadImageToS3(file, t, S3_VOICE_NAME);
      if (voiceFileUrl && rawContactId) {
        const result = await uploadVoice({
          voiceFile: voiceFileUrl,
          rawContact: rawContactId,
        })
        onSuccess?.(result)
        refetch()
      }
    } catch (error) {
      onError?.(error)
    } finally {
      setIsUploading(false)
    }
  }

  // Expose a manual reset for consumers
  const resetRecordingState = () => {
    cleanupMedia();
    setIsUploading(false);
  }

  return {
    isRecording,
    isPaused,
    canStop,
    isUploading,
    isLoading,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    forceStopRecording,
    resetRecordingState,
    saveRecording,
    discardRecording,
    uploadFile,
    hasRecordedAudio,
    recordedAudioUrl,
    recordList,
    refetch
  };
};
